import React, { useMemo, useState } from 'react';

import { Select } from '@aline/common';
import { MenuItem, MenuList, Popover, Tooltip, Typography } from '@mui/material';
import {
  gridColumnVisibilityModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import { matchSorter } from 'match-sorter';
import { ColumnHeaderIcon } from 'src/components/reports/report-detail/ColumnHeader';
import { SearchInput } from 'src/components/SearchInput';
import type { Apollo } from 'src/gql/generated';
import { useGetReportColumnOptions } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import {
  createReportGridColDef,
  getColOptionIdentifier,
  getColumnPositionBeforeActions,
  showColumnAtPosition,
} from '../helpers';
import type { ReportGridColDef } from '../types';

const useStyles = makeStyles()(theme => ({
  columns: {
    display: 'flex',
    flexDirection: 'column',
  },
  emptyColumns: {
    padding: theme.spacing(2),
  },
  iconSpacer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    minWidth: 32,
  },
  menuItem: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
  },
  menuItemDisabled: {
    cursor: 'not-allowed',
    opacity: 0.5,
  },
  paper: {
    maxHeight: 400,
    width: 500,
  },
  promptText: {
    textWrap: 'wrap',
  },
  row: {
    alignItems: 'center',
    display: 'flex',
  },
  searchContainer: {
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.appColors.rule}`,
    padding: theme.spacing(1),
    position: 'sticky',
    top: 0,
    zIndex: theme.zIndex.tooltip,
  },
}));

interface ReportColumnsPickerProps {
  onClose: () => void;
  open: boolean;
  anchorEl?: Element | null;
}

interface VisibleExtractionColumnOption extends Omit<Apollo.ExtractionReportColumn, 'question'> {
  question: Pick<Apollo.ExtractionQuestion, 'id' | 'mode' | 'questionText' | '__typename'>;
}

interface VisibleColumnOption {
  colDef: ReportGridColDef;
  colOption: Apollo.FileFieldColumn | VisibleExtractionColumnOption;
  isAlreadyAdded: boolean;
}

export function ReportColumnsPicker({
  anchorEl,
  onClose,
  open,
}: ReportColumnsPickerProps): JSX.Element {
  const { classes } = useStyles();
  const apiRef = useGridApiContext();
  const [search, setSearch] = useState('');
  const { refetch, reportColumnOptions } = useGetReportColumnOptions();
  const visibilityModel = useGridSelector(apiRef, gridColumnVisibilityModelSelector);

  // Get all columns defined in the report (including hidden ones)
  const visibleColumnFields = useMemo(() => {
    const visibleColumnEntries = Object.entries(visibilityModel).filter(
      ([_, isVisible]) => isVisible
    );
    const visibleColumnFields = visibleColumnEntries
      .filter(([field]) => field !== 'actions')
      .map(([field]) => field);

    return new Set(visibleColumnFields);
  }, [visibilityModel]);

  const searchedColumnOptions = useMemo((): VisibleColumnOption[] => {
    const allColumnOptions = reportColumnOptions.map((o): VisibleColumnOption => {
      const colDef = createReportGridColDef(
        o.__typename === 'ExtractionReportColumn' ? { ...o, displayName: o.question.title } : o
      );

      // Use the original identifier logic
      const columnIdentifier = getColOptionIdentifier(o);

      return {
        colDef,
        colOption: o,
        isAlreadyAdded: visibleColumnFields.has(columnIdentifier),
      };
    });

    if (!search) return allColumnOptions;

    const keys = [
      'colDef.headerName',
      'colOption.displayName',
      'colOption.questionText',
      'colOption.question.title',
    ];

    return matchSorter<VisibleColumnOption>(allColumnOptions, search, { keys });
  }, [reportColumnOptions, search, visibleColumnFields]);

  function handleClose(): void {
    onClose();
    setSearch('');
  }

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      classes={{ paper: classes.paper }}
      open={open}
      slotProps={{
        transition: {
          onEnter: async () => {
            await refetch();
          },
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      onClose={handleClose}
    >
      <div className={classes.searchContainer}>
        <SearchInput placeholder="Search columns..." search={search} setSearch={setSearch} />
      </div>
      <ReportColumns columnOptions={searchedColumnOptions} onClose={handleClose} />
    </Popover>
  );
}

interface ReportColumnsProps {
  columnOptions: VisibleColumnOption[];
  onClose: () => void;
}

function ReportColumns({ columnOptions, onClose }: ReportColumnsProps): JSX.Element {
  const { classes } = useStyles();
  const apiRef = useGridApiContext();

  if (!columnOptions.length)
    return (
      <div className={classes.emptyColumns}>
        <Typography align="center" variant="subtitle1">
          Adjust your search or add a new column.
        </Typography>
      </div>
    );

  return (
    // MenuList provides the MenuListContext that v9 MenuItem requires; v7/v8
    // silently accepted MenuItems inside a plain div, v9 throws.
    <MenuList className={classes.columns} data-cy={Select.report.reportColumnsPicker}>
      {columnOptions.map(({ colDef, colOption, isAlreadyAdded }) => {
        const menuItem = (
          <MenuItem
            key={colDef.field}
            className={`${classes.menuItem} ${isAlreadyAdded ? classes.menuItemDisabled : ''}`}
            disabled={isAlreadyAdded}
            onClick={() => {
              if (isAlreadyAdded) return;

              const targetIndex = getColumnPositionBeforeActions(apiRef);

              onClose();
              showColumnAtPosition(apiRef, colDef.field, targetIndex);
              requestAnimationFrame(() => {
                const actualIndex = apiRef.current.getColumnIndex(colDef.field);
                apiRef.current.scrollToIndexes({ colIndex: actualIndex });
              });
            }}
          >
            <div className={classes.row}>
              <div className={classes.iconSpacer}>
                <ColumnHeaderIcon
                  mode={
                    colOption.__typename === 'ExtractionReportColumn'
                      ? colOption.question.mode
                      : undefined
                  }
                />
              </div>
              <Typography noWrap variant="subtitle1">
                {colDef.headerName}
              </Typography>
            </div>
            <div className={classes.row}>
              <div className={classes.iconSpacer} />
              {colOption.__typename === 'ExtractionReportColumn' && (
                <Typography
                  align="justify"
                  className={classes.promptText}
                  color="textSecondary"
                  variant="body1"
                >
                  {`Prompt: ${colOption.question.questionText || 'None'}`}
                </Typography>
              )}
            </div>
          </MenuItem>
        );

        return isAlreadyAdded ? (
          <Tooltip key={colDef.field} arrow title="Already added to report">
            {menuItem}
          </Tooltip>
        ) : (
          menuItem
        );
      })}
    </MenuList>
  );
}
